"use client";

import Link from "next/link";
import { CheckCircle2, Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  trackAdsCtaClick,
} from "@/components/ads/ads-tracking";
import {
  readAdUserDataConsent,
  readBrowserAttributionWithCurrentUrl,
} from "@/components/analytics/browser-attribution";
import {
  readTurnstileToken,
  resetTurnstile,
  resolveTurnstileSiteKey,
  TurnstileWidget,
} from "@/components/ui/turnstile-widget";
import {
  BUSINESS_BUYING_WINDOW_LABELS,
  BUSINESS_BUYING_WINDOW_VALUES,
  BUSINESS_PRIMARY_NEED_LABELS,
  BUSINESS_PRIMARY_NEED_VALUES,
  BUSINESS_ROLE_LABELS,
  BUSINESS_ROLE_VALUES,
  BUSINESS_TEAM_SIZE_LABELS,
  BUSINESS_TEAM_SIZE_VALUES,
} from "@/lib/business-walkthrough-options";
import { getOrCreatePublicFormSubmissionId } from "@/lib/public-form-submission-id";
import { isAcceptablePhoneInput, MAX_PHONE_INPUT_LENGTH } from "@/lib/uk-phone";

type FieldName =
  | "email"
  | "fullName"
  | "phone"
  | "company"
  | "activeJobs"
  | "teamSize"
  | "role"
  | "primaryNeed"
  | "buyingWindow"
  | "message"
  | "consent";

type FieldErrors = Partial<Record<FieldName, string>>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface BusinessWalkthroughFormProps {
  variant?: string;
  pageSlug?: string;
  experimentKey?: string;
  defaultPrimaryNeed?: (typeof BUSINESS_PRIMARY_NEED_VALUES)[number];
}

interface BusinessWalkthroughResponse {
  success?: boolean;
  deduped?: boolean;
  bookingUrl?: string;
  error?: { fieldErrors?: Record<string, string[]> } | string;
}

function safeBookingUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export function BusinessWalkthroughForm({
  variant = "business-paid-v2",
  pageSlug = "business-ads",
  experimentKey,
  defaultPrimaryNeed,
}: BusinessWalkthroughFormProps = {}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const submissionIdRef = useRef<string | null>(null);
  const posthog = usePostHog();

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrors({});

    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "").trim();
    const fullName = String(formData.get("fullName") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim();
    const company = String(formData.get("company") ?? "").trim();
    const activeJobsRaw = String(formData.get("activeJobs") ?? "").trim();
    const teamSize = String(formData.get("teamSize") ?? "").trim();
    const role = String(formData.get("role") ?? "").trim();
    const primaryNeed = String(formData.get("primaryNeed") ?? "").trim();
    const buyingWindow = String(formData.get("buyingWindow") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    const website = String(formData.get("website") ?? "");
    const activeJobs = Number(activeJobsRaw);
    // Tolerates both input names: Cloudflare's `cf-turnstile-response` in
    // widget mode, and the manual `turnstileToken` fallback the widget renders
    // when no site key is configured (local dev / E2E).
    const turnstileToken = readTurnstileToken(form);

    const localErrors: FieldErrors = {};
    if (!fullName) localErrors.fullName = "Required";
    if (!email || !EMAIL_REGEX.test(email)) {
      localErrors.email = "Enter a valid work email";
    }
    // Optional, so blank is never an error — but a number that could not be a
    // number is caught here rather than costing the whole submission a 400.
    if (!isAcceptablePhoneInput(phone)) {
      localErrors.phone = "Enter a phone number we can reach you on";
    }
    if (!company) localErrors.company = "Required";
    if (!role) localErrors.role = "Choose your role";
    if (!activeJobsRaw || !Number.isInteger(activeJobs) || activeJobs < 1) {
      localErrors.activeJobs = "Enter the number of active jobs";
    }
    if (!teamSize) localErrors.teamSize = "Choose your team size";
    if (!primaryNeed) {
      localErrors.primaryNeed = "Choose the main thing you want to sort";
    }
    if (!consent) {
      localErrors.consent = "Please accept the privacy notice to continue";
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }

    if (resolveTurnstileSiteKey() && !turnstileToken) {
      toast.error("Please complete the verification check before submitting.");
      return;
    }

    submissionIdRef.current = getOrCreatePublicFormSubmissionId(
      submissionIdRef.current,
    );
    const payload: Record<string, unknown> = {
      email,
      fullName,
      company,
      activeJobs,
      role,
      teamSize,
      primaryNeed,
      consent: true,
      turnstileToken,
      website,
      attribution: readBrowserAttributionWithCurrentUrl(),
      adUserDataConsent: readAdUserDataConsent(),
    };
    if (submissionIdRef.current) {
      payload.submissionId = submissionIdRef.current;
    }
    if (phone) payload.phone = phone;
    if (buyingWindow) payload.buyingWindow = buyingWindow;
    if (experimentKey) payload.experimentKey = experimentKey;
    if (message) payload.message = message;

    setSubmitting(true);
    try {
      const response = await fetch("/api/leads/business-walkthrough", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await response
        .json()
        .catch(() => ({}))) as BusinessWalkthroughResponse;

      if (response.ok) {
        setBookingUrl(safeBookingUrl(data.bookingUrl));
        setSubmitted(true);
        toast.success("Your walkthrough request is saved.");
        // Created and recent-duplicate submissions deliberately have the same
        // 202 response, so the browser cannot become a lead-existence oracle
        // or over-count duplicates. Canonical new-lead measurement is the
        // server PostHog event plus the durable Google conversion outbox.
        return;
      }

      if (response.status === 429) {
        toast.error("Too many requests. Please wait a moment and try again.");
        return;
      }

      if (
        response.status === 400 &&
        typeof data.error === "string" &&
        data.error.toLowerCase().includes("turnstile")
      ) {
        toast.error("Verification expired — please try again.");
        return;
      }
      if (
        response.status === 400 &&
        data.error &&
        typeof data.error === "object" &&
        data.error.fieldErrors
      ) {
        const mapped: FieldErrors = {};
        for (const key of Object.keys(data.error.fieldErrors) as FieldName[]) {
          const first = data.error.fieldErrors[key]?.[0];
          if (first) mapped[key] = first;
        }
        setErrors(mapped);
        toast.error("Please check the highlighted fields and try again.");
        return;
      }
      toast.error("Something went wrong. Please try again.");
    } catch {
      toast.error("Couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
      resetTurnstile(turnstileRef.current);
    }
  }

  const inputBaseClass =
    "min-h-[48px] w-full rounded-lg border border-border px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent md:text-sm";
  const fieldClass = (field: FieldName) =>
    errors[field]
      ? `${inputBaseClass} border-danger focus:ring-danger`
      : inputBaseClass;

  function trackBookingHandoff() {
    trackAdsCtaClick(
      {
        variant,
        pageSlug,
        ctaLocation: "walkthrough-success",
        ctaText: "Choose a 20-minute time",
        eventName: "business_walkthrough_booking_started",
        offer: "business",
        emitSignupStart: false,
      },
      posthog,
    );
  }

  if (submitted) {
    return (
      <div className="rounded-2xl border border-done-line bg-done-bg p-8 text-center">
        <CheckCircle2
          className="mx-auto h-10 w-10 text-done"
          aria-hidden="true"
        />
        <h3 className="mt-4 font-heading text-xl font-bold text-navy">
          Your request is saved
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          {bookingUrl
            ? "Choose a 20-minute time now. Your details are already with us if you leave before booking."
            : "We’ll contact you to arrange a practical look at Business around your team and live jobs."}
        </p>
        {bookingUrl ? (
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackBookingHandoff}
            className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-accent px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-accent-dark"
          >
            Choose a 20-minute time
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <div aria-hidden="true" className="sr-only">
        <label htmlFor="business-website">Leave this field empty</label>
        <input
          id="business-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label
            htmlFor="business-fullName"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Full name <span className="text-danger">*</span>
          </label>
          <input
            id="business-fullName"
            name="fullName"
            type="text"
            autoComplete="name"
            aria-invalid={!!errors.fullName}
            aria-describedby={
              errors.fullName ? "business-fullName-error" : undefined
            }
            className={fieldClass("fullName")}
          />
          {errors.fullName ? (
            <p
              id="business-fullName-error"
              className="mt-1 text-xs text-danger"
            >
              {errors.fullName}
            </p>
          ) : null}
        </div>
        <div>
          <label
            htmlFor="business-email"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Work email <span className="text-danger">*</span>
          </label>
          <input
            id="business-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@yourcompany.co.uk"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "business-email-error" : undefined}
            className={fieldClass("email")}
          />
          {errors.email ? (
            <p id="business-email-error" className="mt-1 text-xs text-danger">
              {errors.email}
            </p>
          ) : null}
        </div>
      </div>

      <div>
        <label
          htmlFor="business-phone"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Phone{" "}
          <span className="font-normal text-text-tertiary">(optional)</span>
        </label>
        <input
          id="business-phone"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={MAX_PHONE_INPUT_LENGTH}
          placeholder="07700 900123 or 01234 567890"
          aria-invalid={!!errors.phone}
          aria-describedby={
            errors.phone ? "business-phone-error" : "business-phone-hint"
          }
          className={fieldClass("phone")}
        />
        {errors.phone ? (
          <p id="business-phone-error" className="mt-1 text-xs text-danger">
            {errors.phone}
          </p>
        ) : (
          <p id="business-phone-hint" className="mt-1 text-xs text-muted">
            We’ll call rather than email if you prefer.
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="business-company"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Company <span className="text-danger">*</span>
        </label>
        <input
          id="business-company"
          name="company"
          type="text"
          autoComplete="organization"
          placeholder="Your construction firm"
          aria-invalid={!!errors.company}
          aria-describedby={
            errors.company ? "business-company-error" : undefined
          }
          className={fieldClass("company")}
        />
        {errors.company ? (
          <p id="business-company-error" className="mt-1 text-xs text-danger">
            {errors.company}
          </p>
        ) : null}
      </div>

      <div>
        <label
          htmlFor="business-role"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Your role <span className="text-danger">*</span>
        </label>
        <select
          id="business-role"
          name="role"
          defaultValue=""
          aria-invalid={!!errors.role}
          aria-describedby={errors.role ? "business-role-error" : undefined}
          className={fieldClass("role")}
        >
          <option value="">Select</option>
          {BUSINESS_ROLE_VALUES.map((value) => (
            <option key={value} value={value}>
              {BUSINESS_ROLE_LABELS[value]}
            </option>
          ))}
        </select>
        {errors.role ? (
          <p id="business-role-error" className="mt-1 text-xs text-danger">
            {errors.role}
          </p>
        ) : null}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label
            htmlFor="business-activeJobs"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Active jobs <span className="text-danger">*</span>
          </label>
          <input
            id="business-activeJobs"
            name="activeJobs"
            type="number"
            min={1}
            max={999}
            step={1}
            inputMode="numeric"
            placeholder="e.g. 4"
            aria-invalid={!!errors.activeJobs}
            aria-describedby={
              errors.activeJobs ? "business-activeJobs-error" : undefined
            }
            className={fieldClass("activeJobs")}
          />
          {errors.activeJobs ? (
            <p
              id="business-activeJobs-error"
              className="mt-1 text-xs text-danger"
            >
              {errors.activeJobs}
            </p>
          ) : null}
        </div>
        <div>
          <label
            htmlFor="business-teamSize"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Team size <span className="text-danger">*</span>
          </label>
          <select
            id="business-teamSize"
            name="teamSize"
            defaultValue=""
            aria-invalid={!!errors.teamSize}
            aria-describedby={
              errors.teamSize ? "business-teamSize-error" : undefined
            }
            className={fieldClass("teamSize")}
          >
            <option value="">Select</option>
            {BUSINESS_TEAM_SIZE_VALUES.map((value) => (
              <option key={value} value={value}>
                {BUSINESS_TEAM_SIZE_LABELS[value]}
              </option>
            ))}
          </select>
          {errors.teamSize ? (
            <p
              id="business-teamSize-error"
              className="mt-1 text-xs text-danger"
            >
              {errors.teamSize}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label
            htmlFor="business-primaryNeed"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Main thing to sort <span className="text-danger">*</span>
          </label>
          <select
            id="business-primaryNeed"
            name="primaryNeed"
            defaultValue={defaultPrimaryNeed ?? ""}
            aria-invalid={!!errors.primaryNeed}
            aria-describedby={
              errors.primaryNeed ? "business-primaryNeed-error" : undefined
            }
            className={fieldClass("primaryNeed")}
          >
            <option value="">Select</option>
            {BUSINESS_PRIMARY_NEED_VALUES.map((value) => (
              <option key={value} value={value}>
                {BUSINESS_PRIMARY_NEED_LABELS[value]}
              </option>
            ))}
          </select>
          {errors.primaryNeed ? (
            <p
              id="business-primaryNeed-error"
              className="mt-1 text-xs text-danger"
            >
              {errors.primaryNeed}
            </p>
          ) : null}
        </div>
        <div>
          <label
            htmlFor="business-buyingWindow"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            When do you need it?{" "}
            <span className="font-normal text-text-tertiary">(optional)</span>
          </label>
          <select
            id="business-buyingWindow"
            name="buyingWindow"
            defaultValue=""
            className={fieldClass("buyingWindow")}
          >
            <option value="">Select</option>
            {BUSINESS_BUYING_WINDOW_VALUES.map((value) => (
              <option key={value} value={value}>
                {BUSINESS_BUYING_WINDOW_LABELS[value]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label
          htmlFor="business-message"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          What do you want to improve?{" "}
          <span className="font-normal text-text-tertiary">(optional)</span>
        </label>
        <textarea
          id="business-message"
          name="message"
          rows={3}
          maxLength={2000}
          placeholder="For example: shared RAMS, site files, attendance or subcontractor records."
          className={`${inputBaseClass} min-h-[96px] resize-y`}
        />
      </div>

      <TurnstileWidget className="mt-2" widgetRef={turnstileRef} />

      <div>
        <label
          htmlFor="business-consent"
          className="flex items-start gap-3 text-sm text-muted"
        >
          <input
            id="business-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onChange={(event) => {
              setConsent(event.target.checked);
              if (event.target.checked) {
                setErrors((current) => ({
                  ...current,
                  consent: undefined,
                }));
              }
            }}
            aria-invalid={!!errors.consent}
            aria-describedby={
              errors.consent ? "business-consent-error" : undefined
            }
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-accent focus:ring-2 focus:ring-accent"
          />
          <span>
            I agree to be contacted about The Site Book Business and have read
            the{" "}
            <Link
              href="/legal/privacy"
              className="font-medium text-brand-700 underline hover:text-accent-dark"
            >
              privacy notice
            </Link>
            .
          </span>
        </label>
        {errors.consent ? (
          <p id="business-consent-error" className="mt-1 text-xs text-danger">
            {errors.consent}
          </p>
        ) : null}
      </div>

      <button
        type="submit"
        disabled={!consent || submitting}
        className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-6 py-4 text-base font-bold text-white shadow-lg transition hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          "Request my Business walkthrough"
        )}
      </button>
    </form>
  );
}
